class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 250);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static initInfiniteScrolling() {
    // console.log("Initializing Infinite Scrolling...NEW");
    FacetFiltersForm.infiniteScroll?.destroy();
    FacetFiltersForm.infiniteScroll = new InfiniteScroll({
      productGridSelector: "#product-grid",
      nextPageLinkSelector: "#next-page-link",
      loadingSpinnerSelector: "#loading-spinner",
      threshold: 400,
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.requestVersion = (FacetFiltersForm.requestVersion || 0) + 1;
    const version = FacetFiltersForm.requestVersion;
    FacetFiltersForm.requestController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.requestController = controller;
    FacetFiltersForm.infiniteScroll?.destroy();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");
    const loadingSpinners = document.querySelectorAll(".facets-container .loading__spinner, facet-filters-form .loading__spinner");
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document.getElementById("ProductGridContainer").querySelector(".collection").classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    const grid = document.getElementById("ProductGridContainer");
    grid.setAttribute("aria-busy", "true");
    const error = document.querySelector('[data-facet-error]');
    if (error) error.hidden = true;
    try {
      const responses = await Promise.all(sections.map(async (section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const cached = FacetFiltersForm.filterData.find(entry => entry.url === url);
        const html = cached ? cached.html : await FacetFiltersForm.renderSectionFromFetch(url, controller.signal);
        return { url, html, parsed: new DOMParser().parseFromString(html, "text/html") };
      }));
      if (version !== FacetFiltersForm.requestVersion) return;
      if (responses.some(({ parsed }) => !parsed.getElementById("ProductGridContainer") || !parsed.getElementById("ProductCount"))) throw new Error("Invalid filter response");
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      responses.forEach(({ url, html, parsed }) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.filter(entry => entry.url !== url), { url, html }].slice(-6);
        FacetFiltersForm.renderFilters(parsed, event);
        FacetFiltersForm.renderProductGridContainer(parsed);
        FacetFiltersForm.renderProductCount(parsed);
        if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger();
      });
    } catch (failure) {
      if (version !== FacetFiltersForm.requestVersion || failure.name === "AbortError") return;
      FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
      if (error) {
        error.hidden = false;
        error.querySelector('button').onclick = () => FacetFiltersForm.renderPage(searchParams, null, updateURLHash);
      }
      FacetFiltersForm.initInfiniteScrolling();
    } finally {
      if (version === FacetFiltersForm.requestVersion) {
        grid.removeAttribute("aria-busy");
        grid.querySelector(".collection")?.classList.remove("loading");
        document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(count => count.classList.remove("loading"));
        loadingSpinners.forEach(spinner => spinner.classList.add("hidden"));
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static async renderSectionFromFetch(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
    return response.text();
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = html.getElementById("ProductGridContainer").innerHTML;

    FacetFiltersForm.initInfiniteScrolling();
    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const count = html.getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(".facets-container .loading__spinner, facet-filters-form .loading__spinner");
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter");
    const facetDetailsElementsFromDom = document.querySelectorAll("#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter");

    // Remove facets that are no longer returned from the server
    const fetchedIds = new Set(Array.from(facetDetailsElementsFromFetch, element => element.id));
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!fetchedIds.has(currentElement.id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains("mobile-facets__details") ? `.mobile-facets__close-button` : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    if (window.beyondRefreshSize) window.beyondRefreshSize();
    else window.beyondRefreshFilters?.();
    if (countsToRender?.matches('.beyond-my-size')) document.getElementById(countsToRender.id)?.querySelector('.beyond-size-grid button[aria-pressed="true"]')?.focus();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-facets__open", ".mobile-facets__count", ".sorting"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById("FacetFiltersFormMobile").closest("menu-drawer").bindEvents();
  }

  static renderCounts(source, target) {
    if (target.matches('.beyond-my-size')) {
      target.innerHTML = source.innerHTML;
      return;
    }
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector("show-more-button .label-show-more.hidden"));
      if (isShowingMore) {
        sourceWrapElement.querySelectorAll(".facets__item.hidden").forEach((hiddenItem) => hiddenItem.classList.replace("hidden", "show-more-item"));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    const size = form.querySelector('[data-beyond-size]');
    if (size && size.value !== '__multiple') {
      // Native size checkboxes remain the source for ordinary filter interactions.
      const activeSizes = Array.from(form.querySelectorAll('input[type="checkbox"][name]')).filter(input => input.name === size.dataset.sizeParam && input.checked);
      if (!activeSizes.length && size.value) params.set(size.dataset.sizeParam, size.value);
    }
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm" || form.id === "FacetSortDrawerForm") {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let url = event.currentTarget.href.indexOf("?") == -1 ? "" : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initInfiniteScrolling();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "") minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class FacetScrollbar extends HTMLElement {
  constructor() {
    super();
    this.scrollableList = null;
    this.scrollbar = null;
    this.thumb = null;
  }

  connectedCallback() {
    this.scrollableList = this.previousElementSibling;
    this.scrollbar = this.querySelector(".facet-scrollbar");
    this.thumb = this.querySelector(".facet-scrollbar--thumb");

    if (!this.scrollableList || !this.scrollbar || !this.thumb) {
      console.error("facet-scrollbar requires a sibling .mobile-facets__list and contains .facet-scrollbar, .facet-scrollbar--thumb elements");
      return;
    }

    const detailsElement = this.closest(".mobile-facets__details");
    if (detailsElement?.open) {
      this.checkAndInitializeScrollbar();
    }

    if (detailsElement) {
      detailsElement.addEventListener("toggle", () => {
        if (detailsElement.open) {
          this.checkAndInitializeScrollbar();
        }
      });
    }
  }

  checkAndInitializeScrollbar() {
    const scrollList = this.scrollableList;

    if (scrollList.scrollHeight > 220) {
      this.scrollbar.style.display = "block";
      this.initializeScrollbar();
    } else {
      this.scrollbar.style.display = "none";
    }
  }

  initializeScrollbar() {
    const scrollList = this.scrollableList;
    const thumb = this.thumb;

    const updateThumb = () => {
      const contentHeight = scrollList.scrollHeight;
      const containerHeight = scrollList.offsetHeight;

      if (containerHeight === 0 || contentHeight === 0) return;

      const thumbHeight = Math.max((containerHeight / contentHeight) * containerHeight, 30); // Chiều cao tối thiểu
      thumb.style.height = `${thumbHeight}px`;

      const scrollRatio = scrollList.scrollTop / (contentHeight - containerHeight);
      const thumbTop = scrollRatio * (containerHeight - thumbHeight);
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    scrollList.addEventListener("scroll", updateThumb);

    let isDragging = false;
    let startY, startScrollTop;

    thumb.addEventListener("mousedown", (e) => {
      isDragging = true;
      startY = e.clientY;
      startScrollTop = scrollList.scrollTop;
      document.body.classList.add("no-select");
    });

    document.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      const deltaY = e.clientY - startY;
      const contentHeight = scrollList.scrollHeight;
      const containerHeight = scrollList.offsetHeight;
      const scrollableDistance = contentHeight - containerHeight;
      const thumbMoveRatio = deltaY / containerHeight;
      scrollList.scrollTop = startScrollTop + thumbMoveRatio * scrollableDistance;
    });

    document.addEventListener("mouseup", () => {
      isDragging = false;
      document.body.classList.remove("no-select");
    });

    updateThumb();

    window.addEventListener("resize", updateThumb);
  }
}

customElements.define("facet-scrollbar", FacetScrollbar);


document.addEventListener('click', event => {
  const link = event.target.closest('[data-beyond-clear-sizes]');
  if (!link) return;
  event.preventDefault();
  const url = new URL(location.href);
  ['filter.v.option.size','b_clothing_sizes','b_footwear_sizes','b_truck_deck','page'].forEach(key => url.searchParams.delete(key));
  FacetFiltersForm.renderPage(url.searchParams.toString(), event);
});

window.beyondClearAllFilters = () => {
  document.querySelectorAll('[data-beyond-size]').forEach(select => {
    try { sessionStorage.removeItem('beyond-size-v1-' + select.dataset.sizeScope); } catch {}
  });
  const link = document.querySelector('[data-beyond-clear-all]');
  const destination = new URL(link?.href || location.href, location.origin);
  const collectionPath = location.pathname.match(/^(.*\/collections\/[^/]+)/);
  if (collectionPath) {
    destination.pathname = collectionPath[1];
    destination.search = '';
  } else {
    [...destination.searchParams.keys()].forEach(key => {
      if (key.startsWith('filter.') || key.startsWith('b_') || key === 'page' || key === 'section_id') destination.searchParams.delete(key);
    });
  }
  location.assign(destination.href);
};
document.addEventListener('click', event => {
  if (!event.target.closest('[data-beyond-clear-all]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.beyondClearAllFilters();
}, true);

